import { useState, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewMode = 'login' | 'register' | 'forgot';

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const [view, setView] = useState<ViewMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; fullName?: string; confirmPassword?: string; terms?: string; general?: string }>({});
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const { signIn, signUp, resetPassword, signInWithGoogle, signInWithApple } = useAuth();

  const isLogin = view === 'login';

  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: '', color: 'bg-gray-200' };
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score <= 3) return { score, label: 'Medium', color: 'bg-amber-500' };
    return { score, label: 'Strong', color: 'bg-emerald-500' };
  }, [password]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: { email?: string; password?: string; fullName?: string; confirmPassword?: string; terms?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (view !== 'forgot') {
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }

    if (view === 'register') {
      if (!fullName) {
        newErrors.fullName = 'Full name is required';
      }
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!agreeTerms) {
        newErrors.terms = 'You must agree to the terms';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (view === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          setErrors({ general: error.message });
        } else {
          setResetSent(true);
        }
      } else if (view === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          setErrors({ general: 'Invalid email or password' });
        } else {
          onClose();
          resetForm();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        const { error } = await signUp(email, password, fullName);
        if (error) {
          setErrors({ general: error.message });
        } else {
          setErrors({ general: 'Check your email to confirm your account' });
          setTimeout(() => {
            onClose();
            resetForm();
          }, 3000);
        }
      }
    } catch {
      setErrors({ general: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setAgreeTerms(false);
    setErrors({});
    setResetSent(false);
  };

  const handleInputChange = (field: 'email' | 'password' | 'fullName' | 'confirmPassword', value: string) => {
    if (field === 'email') setEmail(value);
    if (field === 'password') setPassword(value);
    if (field === 'fullName') setFullName(value);
    if (field === 'confirmPassword') setConfirmPassword(value);
    
    setErrors(prev => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const switchMode = (newView: ViewMode) => {
    setView(newView);
    resetForm();
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setErrors({});
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setErrors({ general: error.message });
      }
    } catch {
      setErrors({ general: 'Failed to sign in with Google' });
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setErrors({});
    try {
      const { error } = await signInWithApple();
      if (error) {
        setErrors({ general: error.message });
      }
    } catch {
      setErrors({ general: 'Failed to sign in with Apple' });
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (view === 'forgot') {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl max-w-5xl w-full overflow-hidden shadow-2xl flex relative max-h-[95vh]">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-all shadow-lg cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Left Panel - Visual Brand Area */}
          <div className="hidden lg:flex w-[45%] bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 p-10 flex-col justify-between relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-20 right-10 w-56 h-56 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
            </div>

            {/* Floating Geometric Shapes */}
            <div className="absolute top-20 right-16 w-20 h-24 bg-white/10 backdrop-blur-sm rounded-2xl rotate-12 border border-white/20"></div>
            <div className="absolute bottom-40 left-8 w-16 h-20 bg-white/10 backdrop-blur-sm rounded-2xl -rotate-12 border border-white/20"></div>
            <div className="absolute top-1/3 left-1/4 w-12 h-12 bg-white/20 rounded-xl rotate-45"></div>
            <div className="absolute bottom-1/4 right-1/4 w-8 h-8 border-2 border-white/30 rounded-full"></div>
            <div className="absolute top-2/3 right-12 w-6 h-6 bg-white/30 rounded-lg rotate-12"></div>

            {/* Header Content */}
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <i className="ri-home-smile-line text-white text-2xl"></i>
                </div>
                <span className="text-white text-xl font-bold">iVision</span>
              </div>
            </div>

            {/* Center Content */}
            <div className="relative z-10 flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-lock-unlock-line text-white text-3xl"></i>
                </div>
                <h3 className="text-white text-3xl font-bold mb-4 leading-tight">
                  Reset Your<br />Password
                </h3>
                <p className="text-white/80 text-base leading-relaxed max-w-xs">
                  No worries! It happens to the best of us. We&apos;ll help you get back into your account in no time.
                </p>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                {[
                  { icon: 'ri-mail-send-line', text: 'Enter your email address', step: '1' },
                  { icon: 'ri-inbox-line', text: 'Check your inbox for the link', step: '2' },
                  { icon: 'ri-key-2-line', text: 'Set a new password', step: '3' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                      {item.step}
                    </div>
                    <span className="text-white/90 text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Info */}
            <div className="relative z-10 bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Secure Reset</p>
                  <p className="text-white/60 text-xs">256-bit encrypted connection</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Your reset link expires in 1 hour for security. If you don&apos;t see the email, check your spam folder.
              </p>
            </div>
          </div>

          {/* Right Panel - Forgot Password Form */}
          <div className="w-full lg:w-[55%] p-8 lg:p-12 overflow-y-auto flex flex-col justify-center">
            {/* Back to Login */}
            <button
              onClick={() => switchMode('login')}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors mb-8 cursor-pointer group"
            >
              <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-1"></i>
              <span className="text-sm font-medium">Back to login</span>
            </button>

            {!resetSent ? (
              <>
                {/* Header */}
                <div className="mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center mb-5">
                    <i className="ri-mail-send-line text-teal-600 text-2xl"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password?</h2>
                  <p className="text-gray-500">No worries, we&apos;ll send you reset instructions.</p>
                </div>

                {/* Alert Message */}
                {errors.general && (
                  <div className="mb-6 p-4 rounded-xl flex items-center gap-3 bg-red-50 border border-red-200">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 bg-red-100">
                      <i className="ri-error-warning-fill text-red-600 text-lg"></i>
                    </div>
                    <p className="text-sm font-medium text-red-800">{errors.general}</p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className="ri-mail-line"></i>
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full pl-12 pr-4 py-3.5 border-2 ${
                          errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        } rounded-xl focus:ring-0 focus:border-teal-500 transition-all text-sm`}
                        placeholder="Enter your email address"
                      />
                      {email && !errors.email && /\S+@\S+\.\S+/.test(email) && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-emerald-500">
                          <i className="ri-checkbox-circle-fill"></i>
                        </div>
                      )}
                    </div>
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="relative w-full py-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-500/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group cursor-pointer whitespace-nowrap"
                  >
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                    <span className="relative flex items-center justify-center gap-2 text-white drop-shadow-sm">
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-lg"></i>
                          <span className="font-semibold">Sending...</span>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold">Send Reset Link</span>
                          <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1"></i>
                        </>
                      )}
                    </span>
                  </button>
                </form>

                {/* Back to Sign In */}
                <div className="mt-8 text-center">
                  <p className="text-gray-500 text-sm">
                    Remember your password?{' '}
                    <button
                      onClick={() => switchMode('login')}
                      className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </>
            ) : (
              /* Success State */
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center">
                    <i className="ri-mail-check-line text-teal-600 text-3xl"></i>
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Check Your Email</h2>
                <p className="text-gray-500 mb-2 max-w-sm mx-auto">
                  We&apos;ve sent a password reset link to
                </p>
                <p className="text-gray-900 font-semibold mb-8">{email}</p>

                <div className="bg-gray-50 rounded-2xl p-5 mb-8 text-left max-w-sm mx-auto">
                  <p className="text-sm text-gray-600 mb-3 font-medium">Didn&apos;t receive the email?</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-gray-500">
                      <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      Check your spam or junk folder
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-500">
                      <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      Make sure you entered the correct email
                    </li>
                    <li className="flex items-start gap-2 text-sm text-gray-500">
                      <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                      The link expires in 1 hour
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => {
                    setResetSent(false);
                    setEmail('');
                  }}
                  className="relative w-full max-w-sm mx-auto py-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-500/40 active:scale-[0.98] group cursor-pointer whitespace-nowrap"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                  <span className="relative flex items-center justify-center gap-2 text-white drop-shadow-sm font-semibold">
                    <i className="ri-refresh-line text-lg"></i>
                    Resend Email
                  </span>
                </button>

                <div className="mt-6">
                  <button
                    onClick={() => switchMode('login')}
                    className="text-teal-600 hover:text-teal-700 font-semibold text-sm cursor-pointer flex items-center gap-1 mx-auto"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Login View
  if (isLogin) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl max-w-5xl w-full overflow-hidden shadow-2xl flex relative max-h-[95vh]">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-all shadow-lg cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Left Panel - Visual Brand Area */}
          <div className="hidden lg:flex w-[45%] bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 p-10 flex-col justify-between relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0">
              <div className="absolute top-10 left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-20 right-10 w-56 h-56 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
            </div>

            {/* Floating Geometric Shapes */}
            <div className="absolute top-20 right-16 w-20 h-24 bg-white/10 backdrop-blur-sm rounded-2xl rotate-12 border border-white/20"></div>
            <div className="absolute bottom-40 left-8 w-16 h-20 bg-white/10 backdrop-blur-sm rounded-2xl -rotate-12 border border-white/20"></div>
            <div className="absolute top-1/3 left-1/4 w-12 h-12 bg-white/20 rounded-xl rotate-45"></div>
            <div className="absolute bottom-1/4 right-1/4 w-8 h-8 border-2 border-white/30 rounded-full"></div>
            <div className="absolute top-2/3 right-12 w-6 h-6 bg-white/30 rounded-lg rotate-12"></div>

            {/* Header Content */}
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <i className="ri-home-smile-line text-white text-2xl"></i>
                </div>
                <span className="text-white text-xl font-bold">iVision</span>
              </div>
            </div>

            {/* Center Content */}
            <div className="relative z-10 flex-1 flex flex-col justify-center">
              <div className="mb-8">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-magic-line text-white text-3xl"></i>
                </div>
                <h3 className="text-white text-3xl font-bold mb-4 leading-tight">
                  Welcome Back<br />We Missed You
                </h3>
                <p className="text-white/80 text-base leading-relaxed max-w-xs">
                  Sign in to continue transforming your property listings with stunning virtual staging.
                </p>
              </div>
            </div>

            {/* Bottom Testimonial */}
            <div className="relative z-10 bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-amber-300 text-sm"></i>
                ))}
              </div>
              <p className="text-white text-sm leading-relaxed mb-4">
                &quot;iVision transformed our empty listings into stunning showcases. Our properties now sell 40% faster!&quot;
              </p>
              <div className="flex items-center gap-3">
                <img
                  src="https://public.readdy.ai/ai/img_res/8d795fdf8ef5b5fa36d8ca42fef8e310.jpg"
                  alt="Sarah Mitchell"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-white font-semibold text-sm">Sarah Mitchell</p>
                  <p className="text-white/60 text-xs">Real Estate Agent, NYC</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-[55%] p-8 lg:p-12 overflow-y-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-8 lg:hidden">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <i className="ri-home-smile-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold text-gray-900">iVision</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
              <p className="text-gray-500">Sign in to upload your pictures</p>
            </div>

            {/* Alert Message */}
            {errors.general && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                errors.general.includes('Check your email') 
                  ? 'bg-emerald-50 border border-emerald-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                  errors.general.includes('Check your email')
                    ? 'bg-emerald-100'
                    : 'bg-red-100'
                }`}>
                  <i className={`${
                    errors.general.includes('Check your email')
                      ? 'ri-checkbox-circle-fill text-emerald-600'
                      : 'ri-error-warning-fill text-red-600'
                  } text-lg`}></i>
                </div>
                <p className={`text-sm font-medium ${
                  errors.general.includes('Check your email') ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {errors.general}
                </p>
              </div>
            )}

            {/* Social Sign In */}
            <div className="flex gap-4 mb-6">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm font-medium text-gray-700">Google</span>
              </button>
              <button
                onClick={handleAppleSignIn}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-apple-fill text-xl text-gray-900"></i>
                <span className="text-sm font-medium text-gray-700">Apple</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or continue with email</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-mail-line"></i>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-12 pr-4 py-3.5 border-2 ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                    } rounded-xl focus:ring-0 focus:border-teal-500 transition-all text-sm`}
                    placeholder="you@example.com"
                  />
                  {email && !errors.email && /\S+@\S+\.\S+/.test(email) && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-emerald-500">
                      <i className="ri-checkbox-circle-fill"></i>
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-line"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full pl-12 pr-4 py-3.5 border-2 ${
                      errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                    } rounded-xl focus:ring-0 focus:border-teal-500 transition-all text-sm`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 rounded-md border-2 border-gray-300 group-hover:border-gray-400 peer-checked:bg-teal-500 peer-checked:border-teal-500 flex items-center justify-center transition-all">
                      <i className="ri-check-line text-white text-sm opacity-0 peer-checked:opacity-100"></i>
                    </div>
                  </div>
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-500/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group cursor-pointer whitespace-nowrap"
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                
                <span className="relative flex items-center justify-center gap-2 text-white drop-shadow-sm">
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      <span className="font-semibold">Signing in...</span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Sign In</span>
                      <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1"></i>
                    </>
                  )}
                </span>
              </button>
            </form>

            {/* Sign Up Link */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-sm">
                Don&apos;t have an account?{' '}
                <button
                  onClick={() => switchMode('register')}
                  className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                >
                  Sign up
                </button>
              </p>
            </div>

            {/* Bottom Stats - Mobile Only */}
            <div className="mt-8 pt-8 border-t border-gray-100 lg:hidden">
              <div className="flex items-center justify-between text-center">
                <div>
                  <p className="text-gray-900 text-xl font-bold">10K+</p>
                  <p className="text-gray-500 text-xs">Properties</p>
                </div>
                <div className="w-px h-10 bg-gray-200"></div>
                <div>
                  <p className="text-gray-900 text-xl font-bold">98%</p>
                  <p className="text-gray-500 text-xs">Satisfaction</p>
                </div>
                <div className="w-px h-10 bg-gray-200"></div>
                <div>
                  <p className="text-gray-900 text-xl font-bold">24h</p>
                  <p className="text-gray-500 text-xs">Turnaround</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create Account View
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl flex relative max-h-[92vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-all shadow-lg cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Left Panel - Visual Brand Area */}
        <div className="hidden lg:flex w-[42%] bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 p-8 flex-col justify-between relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0">
            <div className="absolute top-8 left-8 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-16 right-8 w-44 h-44 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-white/5 rounded-full blur-3xl"></div>
          </div>

          {/* Floating Geometric Shapes */}
          <div className="absolute top-16 right-12 w-16 h-20 bg-white/10 backdrop-blur-sm rounded-xl rotate-12 border border-white/20"></div>
          <div className="absolute bottom-32 left-6 w-12 h-16 bg-white/10 backdrop-blur-sm rounded-xl -rotate-12 border border-white/20"></div>
          <div className="absolute top-1/3 left-1/4 w-10 h-10 bg-white/20 rounded-lg rotate-45"></div>
          <div className="absolute bottom-1/4 right-1/4 w-6 h-6 border-2 border-white/30 rounded-full"></div>
          <div className="absolute top-2/3 right-10 w-5 h-5 bg-white/30 rounded-lg rotate-12"></div>

          {/* Header Content */}
          <div className="relative z-10">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-rocket-line text-white text-xl"></i>
              </div>
              <span className="text-white text-lg font-bold">iVision</span>
            </div>
          </div>

          {/* Center Content */}
          <div className="relative z-10 flex-1 flex flex-col justify-center">
            <div className="mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white text-2xl font-bold mb-3 leading-tight">
                Start Your<br />Journey Today
              </h3>
              <p className="text-white/80 text-sm leading-relaxed max-w-xs">
                Join thousands of real estate professionals transforming their listings with stunning virtual staging.
              </p>
            </div>

            {/* Feature List */}
            <div className="space-y-3">
              {[
                { icon: 'ri-time-line', text: '24-hour turnaround time' },
                { icon: 'ri-palette-line', text: 'Multiple design styles' },
                { icon: 'ri-shield-star-line', text: '100% satisfaction guarantee' },
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                    <i className={`${feature.icon} text-white text-xs`}></i>
                  </div>
                  <span className="text-white/90 text-xs font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Testimonial */}
          <div className="relative z-10 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-amber-300 text-xs"></i>
              ))}
            </div>
            <p className="text-white text-xs leading-relaxed mb-3">
              &quot;Signing up was the best decision for my business. The virtual staging quality is incredible!&quot;
            </p>
            <div className="flex items-center gap-2.5">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20a%20smiling%20real%20estate%20agent%20man%20with%20dark%20hair%20wearing%20business%20suit%20against%20a%20neutral%20gray%20background%20studio%20lighting%20high%20quality&width=100&height=100&seq=testimonial2&orientation=squarish"
                alt="Michael Chen"
                className="w-8 h-8 rounded-full object-cover"
              />
              <div>
                <p className="text-white font-semibold text-xs">Michael Chen</p>
                <p className="text-white/60 text-[10px]">Broker, Los Angeles</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Registration Form */}
        <div className="w-full lg:w-[58%] p-6 lg:p-9 overflow-y-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-6 lg:hidden">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <i className="ri-home-smile-line text-white text-lg"></i>
              </div>
              <span className="text-lg font-bold text-gray-900">iVision</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1.5">Create Account</h2>
            <p className="text-gray-500 text-sm">Join us today and transform your listings</p>
          </div>

          {/* Alert Message */}
          {errors.general && (
            <div className={`mb-5 p-3 rounded-lg flex items-center gap-2.5 ${
              errors.general.includes('Check your email') 
                ? 'bg-emerald-50 border border-emerald-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${
                errors.general.includes('Check your email')
                  ? 'bg-emerald-100'
                  : 'bg-red-100'
              }`}>
                <i className={`${
                  errors.general.includes('Check your email')
                    ? 'ri-checkbox-circle-fill text-emerald-600'
                    : 'ri-error-warning-fill text-red-600'
                } text-base`}></i>
              </div>
              <p className={`text-xs font-medium ${
                errors.general.includes('Check your email') ? 'text-emerald-800' : 'text-red-800'
              }`}>
                {errors.general}
              </p>
            </div>
          )}

          {/* Social Sign Up */}
          <div className="flex gap-3 mb-5">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2.5 py-2.5 px-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-xs font-medium text-gray-700">Google</span>
            </button>
            <button
              onClick={handleAppleSignIn}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2.5 py-2.5 px-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-apple-fill text-lg text-gray-900"></i>
              <span className="text-xs font-medium text-gray-700">Apple</span>
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white text-gray-500">or continue with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-user-line text-sm"></i>
                </div>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className={`w-full pl-10 pr-3 py-3 border-2 ${
                    errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  } rounded-lg focus:ring-0 focus:border-teal-500 transition-all text-xs`}
                  placeholder="Enter your full name"
                />
                {fullName && !errors.fullName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-emerald-500">
                    <i className="ri-checkbox-circle-fill text-sm"></i>
                  </div>
                )}
              </div>
              {errors.fullName && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line text-xs"></i>
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-mail-line text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full pl-10 pr-3 py-3 border-2 ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  } rounded-lg focus:ring-0 focus:border-teal-500 transition-all text-xs`}
                  placeholder="you@example.com"
                />
                {email && !errors.email && /\S+@\S+\.\S+/.test(email) && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-emerald-500">
                    <i className="ri-checkbox-circle-fill text-sm"></i>
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line text-xs"></i>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-line text-sm"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={`w-full pl-10 pr-10 py-3 border-2 ${
                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  } rounded-lg focus:ring-0 focus:border-teal-500 transition-all text-xs`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                </button>
              </div>
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      ></div>
                    </div>
                    <span className={`text-[10px] font-medium ${
                      passwordStrength.score <= 2 ? 'text-red-500' : 
                      passwordStrength.score <= 3 ? 'text-amber-500' : 'text-emerald-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { check: password.length >= 6, text: '6+ chars' },
                      { check: /[A-Z]/.test(password), text: 'Uppercase' },
                      { check: /[0-9]/.test(password), text: 'Number' },
                      { check: /[^A-Za-z0-9]/.test(password), text: 'Symbol' },
                    ].map((req, index) => (
                      <span 
                        key={index}
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          req.check 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {req.check && <i className="ri-check-line mr-0.5"></i>}
                        {req.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line text-xs"></i>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-line text-sm"></i>
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={`w-full pl-10 pr-10 py-3 border-2 ${
                    errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  } rounded-lg focus:ring-0 focus:border-teal-500 transition-all text-xs`}
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                </button>
              </div>
              {confirmPassword && password === confirmPassword && (
                <p className="mt-1.5 text-xs text-emerald-600 flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill text-xs"></i>
                  Passwords match
                </p>
              )}
              {errors.confirmPassword && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line text-xs"></i>
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms Checkbox */}
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => {
                      setAgreeTerms(e.target.checked);
                      setErrors(prev => ({ ...prev, terms: undefined }));
                    }}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded flex items-center justify-center transition-all ${
                    agreeTerms 
                      ? 'bg-teal-500 border-teal-500' 
                      : errors.terms 
                        ? 'border-red-300 bg-red-50 border-2' 
                        : 'border-gray-300 group-hover:border-gray-400 border-2'
                  }`}>
                    {agreeTerms && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                </div>
                <span className="text-xs text-gray-600">
                  I agree to the{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 font-medium">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 font-medium">Privacy Policy</a>
                </span>
              </label>
              {errors.terms && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line text-xs"></i>
                  {errors.terms}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full py-3.5 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-teal-500/40 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed group cursor-pointer whitespace-nowrap"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
              
              <span className="relative flex items-center justify-center gap-2 text-white drop-shadow-sm">
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    <span className="font-semibold text-sm">Creating account...</span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-sm">Create Account</span>
                    <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
                  </>
                )}
              </span>
            </button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <button
                onClick={() => switchMode('login')}
                className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
              >
                Sign in
              </button>
            </p>
          </div>

          {/* Bottom Stats - Mobile Only */}
          <div className="mt-6 pt-6 border-t border-gray-100 lg:hidden">
            <div className="flex items-center justify-between text-center">
              <div>
                <p className="text-gray-900 text-lg font-bold">10K+</p>
                <p className="text-gray-500 text-xs">Clients</p>
              </div>
              <div className="w-px h-8 bg-gray-200"></div>
              <div>
                <p className="text-gray-900 text-lg font-bold">50K+</p>
                <p className="text-gray-500 text-xs">Photos</p>
              </div>
              <div className="w-px h-8 bg-gray-200"></div>
              <div>
                <p className="text-gray-900 text-lg font-bold">4.9</p>
                <p className="text-gray-500 text-xs">Rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
